import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {func, object, string} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {toDateString, toISOString} from '../../utils.js';

const DateDisplay = ({since, before}) => {
  let content;
  if (since && before) {
    content = (
      <>
        {toDateString(since)} &mdash; {toDateString(before)}
      </>
    );
  } else if (since) {
    content = `since ${toDateString(since)}`;
  } else if (before) {
    content = `before ${toDateString(before)}`;
  } else {
    content = 'all dates';
  }
  return <Box sx={{display: 'flex', alignItems: 'center'}}>{content}</Box>;
};

DateDisplay.propTypes = {
  since: string,
  before: string,
};

function DateRange({sx, onChange, since, before}) {
  const [sinceDateString, setSinceDateString] = useState(toDateString(since));
  const [beforeDateString, setBeforeDateString] = useState(
    toDateString(before),
  );

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleDateChange = useCallback(event => {
    const value = event.target.value;
    switch (event.target.name) {
      case 'since': {
        setSinceDateString(value);
        break;
      }
      case 'before': {
        setBeforeDateString(value);
        break;
      }
      default: {
        // pass
      }
    }
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClearDateClick = useCallback(() => {
    setSinceDateString();
    setBeforeDateString();
    onChange({since: '', before: ''});
    handleClose();
  }, [onChange]);

  const handleApplyDateClick = useCallback(() => {
    onChange({
      since: toISOString(sinceDateString),
      before: toISOString(beforeDateString),
    });
    handleClose();
  }, [onChange, sinceDateString, beforeDateString]);

  const color = before || since ? 'primary' : 'secondary';

  return (
    <Box sx={sx}>
      <Button
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        startIcon={<DateRangeIcon />}
        size="small"
        color={color}
        endIcon={<ArrowDropDownIcon />}
      >
        <DateDisplay since={since} before={before} />
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 2,
        }}
      >
        <Box sx={{padding: 3}}>
          <Typography variant="overline">Date published</Typography>
          <Divider />

          <Box sx={{marginTop: 3}}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="since"
                  value={sinceDateString || ''}
                  label="Since"
                  type="date"
                  size="small"
                  onChange={handleDateChange}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  value={beforeDateString || ''}
                  name="before"
                  label="Before"
                  type="date"
                  size="small"
                  onChange={handleDateChange}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} />
              <Grid item xs={12} sm={6}>
                <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    sx={{marginRight: 3}}
                    onClick={handleClearDateClick}
                    disabled={!sinceDateString && !beforeDateString}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleApplyDateClick}
                    disabled={!sinceDateString && !beforeDateString}
                  >
                    Apply
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

DateRange.propTypes = {
  sx: object,
  onChange: func.isRequired,
  since: string,
  before: string,
};

export default DateRange;
